import { useEffect, useReducer, useRef } from 'react';

import { createPagination } from 'pkg/utils/pagination';

import type { Action } from 'pkg/api/models/auto/action';
import type { Collection } from 'pkg/api/models/collection';
import { hasNext, hasPrev } from 'pkg/api/models/collection/guards';
import { next, prev, byPage } from 'pkg/api/models/collection/utils';
import { filterDuplicatesBy, filterDuplicatesByKey } from 'pkg/arrays';
import * as sdk from 'pkg/core/sdk';
import { useSorting } from 'pkg/hooks/use_sorting';
import * as logger from 'pkg/logger';
import { pushState } from 'pkg/router/state';

import { useApiConfig } from 'components/application/apiconfig';

import type { CollectionPagination } from './pagination';
import type { CollectionResponse } from './response';
import type { CollectionSortOrder } from './sorting';
import type { CollectionState } from './state';
import { createInitialState } from './state';
import { buildEndpointURL, updateBrowserURL } from './utils';

export interface CollectionOptions<T> {
	queryParams?: URLSearchParams;
	showAllResults?: boolean;
	showOnlySelected?: boolean;
	defaultSelectedRecords?: T[];
	count?: number;
	stateInURL?: boolean;
	defaultSortBy?: string;
	defaultSortByOrder?: CollectionSortOrder;
	/**
	 * Which allowed actions should be loaded from the backend, the response will include
	 * a subset of this list with the allowed actions.
	 */
	actions?: Action[];
	/**
	 * Transform each record after fetching. Useful for normalizing API responses.
	 */
	transform?: (record: T) => T;
}

// Records from public endpoints carry a publicId instead of an internal id
// (id is 0 on all of them), so dedupe on whichever identity the record has.
const recordIdentity = (record: { id?: number | string; publicId?: string }) =>
	record.publicId || record.id;

/**
 *	A change to the collection's state: the fields to set, or a function that
 *	works them out from the state as it is when React applies it, so a change
 *	queued before a re-render isn't lost to the next one. Null changes nothing.
 */
type CollectionUpdate<T> =
	| Partial<CollectionState<T>>
	| ((current: CollectionState<T>) => Partial<CollectionState<T>> | null);

export function useCollection<
	T extends { id?: number | string; publicId?: string },
>(endpoint: string, options: CollectionOptions<T> = {}): CollectionResponse<T> {
	const apiConfig = useApiConfig();

	// Note: reset is defined later in this component, but this closure
	// captures it by reference and will resolve correctly when called
	const onBeforeSort = () => {
		if (options.showAllResults) {
			reset();
		}
	};

	const sort = useSorting({
		defaultSortBy: options.defaultSortBy,
		defaultSortByOrder: options.defaultSortByOrder,
		stateInURL: options.stateInURL,
		onBeforeSort,
	});

	const initialState = createInitialState<T>(
		endpoint,
		options,
		sort.column,
		sort.order
	);
	const initialRecords = useRef<T[]>([]);
	const [state, setState] = useReducer(
		(current: CollectionState<T>, update: CollectionUpdate<T>) => {
			const changes = typeof update === 'function' ? update(current) : update;

			return changes ? { ...current, ...changes } : current;
		},
		initialState
	);

	const fetchData = async () => {
		if (!state.isLoading) {
			setState({ isLoading: true });
		}

		if (!state.url || state.url.includes('undefined')) {
			setState({ isLoading: false });
			return;
		}

		let r: Response;
		try {
			r = await sdk.get(state.url, {}, {}, {});
		} catch (e) {
			logger.error(new Error('failed to make request', { cause: e }), {
				url: state.url,
			});
			setState({
				hasError: true,
				isLoading: false,
			});
			return;
		}

		if ([426, 503].includes(r?.status)) {
			apiConfig.refreshConfig();
			return;
		}

		if (r?.status >= 500 && r?.status < 600) {
			logger.error(new Error(`server error ${r.statusText}`), {
				statusText: r.statusText,
				status: r.status,
				url: state.url,
			});
			setState({
				hasError: true,
				isLoading: false,
			});
			return;
		}

		let parsedResponse;
		try {
			parsedResponse = await r.json();
		} catch (e) {
			logger.error(new Error('failed to parse json', { cause: e }), {
				url: state.url,
			});
			setState({
				hasError: true,
				isLoading: false,
			});
			return;
		}

		const newState: Partial<CollectionState<T>> = {
			collection: state.collection,
			response: r,
			isLoading: false,
			hasError: false,
		};

		const hasRecords = 'records' in parsedResponse;

		if (r.ok && !hasRecords) {
			logger.error(
				new Error(`Non-collection response from endpoint: ${state.url}`)
			);
		}

		if (r.ok && hasRecords) {
			const applyTransform = (records: T[]): T[] =>
				options.transform ? records.map(options.transform) : records;

			if (options.showAllResults) {
				const newCollection = parsedResponse as Collection<T>;
				newState.records = filterDuplicatesBy<T>(
					[...state.records, ...applyTransform(newCollection.records)],
					recordIdentity
				);
			} else {
				newState.records = applyTransform(parsedResponse.records as T[]);
			}

			newState.collection = parsedResponse as Collection<T>;

			// Capture initial records only on first successful fetch with records
			if (
				initialRecords.current.length === 0 &&
				parsedResponse.records?.length > 0
			) {
				initialRecords.current = parsedResponse.records as T[];
			}
		} else {
			newState.hasError = true;
		}

		setState(newState);
	};

	const reset = () => {
		const s = { ...state };
		s.page = initialState.page;
		setState({
			page: initialState.page,
			records: [],
			appendedRecords: [],
			prependedRecords: [],
			collection: {} as Collection<T>,
			url: buildEndpointURL(endpoint, options, s, sort.column, sort.order),
		});
	};

	useEffect(() => {
		if (!options.stateInURL) return;

		const handlePopState = () => {
			const qs = new URLSearchParams(window.location.search);
			const urlPage = qs.has('page') ? Number.parseInt(qs.get('page'), 10) : 1;

			if (urlPage !== state.page) {
				setState({
					page: urlPage,
					url: buildEndpointURL(
						endpoint,
						options,
						{ ...state, page: urlPage },
						sort.column,
						sort.order
					),
				});
			}
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [options.stateInURL, state.page, endpoint, sort.column, sort.order]);

	useEffect(() => {
		fetchData();
	}, [state.url]);

	useEffect(() => {
		setState({
			url: buildEndpointURL(endpoint, options, state, sort.column, sort.order),
		});
	}, [endpoint, sort.column, sort.order]);

	// reset back to page 1 when filters/params change
	useEffect(() => {
		reset();
	}, [options.queryParams?.toString(), options.count, endpoint]);

	const isAllSelected = options.showOnlySelected
		? true
		: state.records.every((record) =>
				state.selectedRecords.find((r) => r.id === record.id)
			);

	let records = state.records;

	if (state.appendedRecords.length) {
		records = [...records, ...state.appendedRecords];
	}

	if (state.prependedRecords.length) {
		records = [...state.prependedRecords, ...records];
	}

	const totalCount = state.collection?.meta?.totalCount || 0;
	const recordsPerPage =
		(state.collection?.meta?.recordsPerPage as number) || undefined;
	const meta = state.collection?.meta;

	const pagination: CollectionPagination = createPagination({
		currentPage: state.page,
		totalCount: totalCount as number,
		itemsPerPage: recordsPerPage,
		hasNext: hasNext(state.collection),
		hasPrev: hasPrev(state.collection),
		onNext: () => {
			const nextPage = state.page + 1;
			setState({
				isLoading: true,
				page: nextPage,
				url: next(state.collection),
			});
			if (options.stateInURL) {
				pushState(updateBrowserURL({ ...state, page: nextPage }));
			}
		},
		onPrev: () => {
			const prevPage = state.page - 1;
			setState({
				isLoading: true,
				page: prevPage,
				url: prev(state.collection),
			});
			if (options.stateInURL) {
				pushState(updateBrowserURL({ ...state, page: prevPage }));
			}
		},
		onGoToPage: (page: number) => {
			setState({
				page,
				url: byPage(state.collection, page),
			});
			if (options.stateInURL) {
				pushState(updateBrowserURL({ ...state, page }));
			}
		},
	});

	const includesRecord = (recordId: number): boolean => {
		return records.findIndex((item: T) => item.id === recordId) > -1;
	};

	// The mutators below work from the state React applies them to, not the
	// state of the render they came from, so two in a row both land.
	const removeRecord = (recordId: number) => {
		setState((current) => ({
			records: current.records.filter((item: T) => item.id !== recordId),
			appendedRecords: current.appendedRecords.filter(
				(item: T) => item.id !== recordId
			),
			prependedRecords: current.prependedRecords.filter(
				(item: T) => item.id !== recordId
			),
		}));
	};

	const replaceRecord = (newRecord: T | T[], key: keyof T = 'id') => {
		let replaceRecords: T[] = [];
		if (!Array.isArray(newRecord)) {
			replaceRecords = [newRecord];
		} else {
			replaceRecords = newRecord;
		}

		const replacer = (r: T) => {
			const newR = replaceRecords.find((r2) => r2[key] === r[key]);
			if (newR) {
				return newR;
			}

			return r;
		};

		setState((current) => ({
			records: current.records.map(replacer),
			appendedRecords: current.appendedRecords.map(replacer),
			prependedRecords: current.prependedRecords.map(replacer),
		}));
	};

	const replaceInRecord = (recordId: number, key: keyof T, newRecord: T) => {
		const replacer = (oldRecord: T) => {
			if (oldRecord.id !== recordId) {
				return oldRecord;
			}
			// This should only be done if the key is an array - there may be cases when we want to
			// replace only a object
			const innerRecord = oldRecord[key] as any[any];

			const index = innerRecord.findIndex(
				(inner: T) => inner.id === newRecord.id
			);
			innerRecord[index] = newRecord;

			oldRecord[key] = innerRecord;

			return oldRecord;
		};

		setState((current) => ({
			records: current.records.map(replacer),
			appendedRecords: current.appendedRecords.map(replacer),
			prependedRecords: current.prependedRecords.map(replacer),
		}));
	};

	const addRecord = (newRecord: T, prepend?: boolean) => {
		setState((current) => {
			const list = prepend ? current.prependedRecords : current.appendedRecords;

			if (list.some((r) => r.id === newRecord.id)) {
				return null;
			}

			return prepend
				? { prependedRecords: [newRecord, ...current.prependedRecords] }
				: { appendedRecords: [...current.appendedRecords, newRecord] };
		});
	};

	// Kept with the appended records: a page fetch in flight writes back the
	// records it started from, which would drop these. The dedupe below lets
	// the appended copy of a record replace the one fetched with the page.
	const mergeRecords = (
		newRecords: T[],
		accept: (incoming: T, held: T | undefined) => boolean = () => true
	) => {
		setState((current) => {
			// The copy of each record the dedupe below would show.
			const held = new Map(
				[
					...current.prependedRecords,
					...current.records,
					...current.appendedRecords,
				].map((record) => [recordIdentity(record), record])
			);
			const accepted = newRecords.filter((record) =>
				accept(record, held.get(recordIdentity(record)))
			);

			if (accepted.length === 0) {
				return null;
			}

			return {
				appendedRecords: filterDuplicatesBy<T>(
					[...current.appendedRecords, ...accepted],
					recordIdentity
				),
			};
		});
	};

	const selectedRecordIds = state.selectedRecords.map((record) => record.id);

	const selectOnlyOne = (id: number) => {
		if (selectedRecordIds.includes(id)) {
			setState({
				selectedRecords: [],
			});
		} else {
			setState({
				selectedRecords: [state.records.find((result: T) => result.id === id)],
			});
		}
	};

	const selectSingle = (id: number) => {
		if (selectedRecordIds.includes(id)) {
			setState({
				selectedRecords: state.selectedRecords.filter(
					(record) => record.id !== id
				),
			});
		} else {
			setState({
				selectedRecords: [
					...state.selectedRecords,
					state.records.find((record) => record.id === id),
				],
			});
		}
	};

	const selectMultiple = (ids: number[]) => {
		const idSet = new Set(ids);
		const recordMap = new Map<number, T>();

		// Keep existing selected records that are in the new ids
		for (const record of state.selectedRecords) {
			if (idSet.has(record.id as number)) {
				recordMap.set(record.id as number, record);
			}
		}

		// Add current page records that are in the new ids
		for (const record of state.records) {
			if (idSet.has(record.id as number)) {
				recordMap.set(record.id as number, record);
			}
		}

		setState({
			selectedRecords: Array.from(recordMap.values()),
		});
	};

	const selectAll = () => {
		if (options.showOnlySelected) {
			setState({ selectedRecords: [] });
		} else {
			if (isAllSelected) {
				setState({
					selectedRecords: state.records.filter(
						(record) => !state.records.includes(record)
					),
				});
			} else {
				setState({
					selectedRecords: filterDuplicatesByKey(
						[...state.selectedRecords, ...state.records],
						'id'
					),
				});
			}
		}
	};

	const deselectAll = () => setState({ selectedRecords: [] });

	records = filterDuplicatesBy<T>(records, recordIdentity);

	if (options.showOnlySelected) {
		records = state.selectedRecords;
	}

	return {
		records: records,
		initialRecords: initialRecords.current,
		meta,
		reset,
		refresh: fetchData,
		includesRecord,
		removeRecord,
		replaceRecord,
		replaceInRecord,
		addRecord,
		mergeRecords,
		response: state.response,
		isLoading: state.isLoading,
		sort,
		pagination,
		selection: {
			selectedRecords: state.selectedRecords.map((record) => record.id),
			selectSingle,
			selectOnlyOne,
			selectAll,
			isSelected: (id: number) =>
				state.selectedRecords.map((record) => record.id).includes(id),
			isAllSelected,
			deselectAll,
			selectMultiple,
		},
		selectedRecords: state.selectedRecords,
		hasError: state.hasError,
	};
}
